// copiato da src/components/solitario/TutorialSolitario.tsx:36-176 @ cad51507af021388ceb3a5de34eb61259665077d (`Rettangolo`, `rettangoloDi`, `uguali`, `Misurati`, `Accesi`), generalizzato:
// i bersagli sono del gioco (`bersaglio(b, tavolo)` rende un Element qualunque, anche un nodo SVG), l'accensione non usa la classe `tav-invito` del modulo tavolo (una zona non importa da
// un'altra) e il fantasma e` un riquadro grande quanto l'origine, con dentro cio` che il gioco vuole (`rendiFantasma`).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ArrowRight } from 'lucide-react';

import { useMovimentoRidotto } from '@/hooks/useMovimentoRidotto';

interface Rettangolo {
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * Solo per un bersaglio RUOTATO (una carta del ventaglio): l'angolo in gradi, e allora `x, y, w, h` sono la sua scatola NON ruotata, col centro sul centro vero (v. `sagomaRuotata`). Assente per un bersaglio assiale:
   * il rettangolo e` quello di sempre e l'evidenza ha il DOM di sempre.
   */
  angolo?: number;
  /** Solo per un bersaglio ruotato che un antenato TAGLIA: il ritaglio (in coordinate della radice), dentro cui l'anello ruotato si disegna. */
  ritaglio?: Rettangolo;
  /** Solo per un bersaglio che ha carte DAVANTI (v. `copertureDi`): le loro sagome (in coordinate della radice), dove l'evidenza non si disegna. Assente: l'evidenza ha il DOM di sempre. */
  coperture?: Sagoma[];
}

/** La sagoma di un elemento che copre il bersaglio: il centro, il lato di layout, l'angolo (0 se assiale) e il raggio degli angoli, in coordinate della radice. */
interface Sagoma {
  cx: number;
  cy: number;
  w: number;
  h: number;
  angolo: number;
  raggio: number;
}

/**
 * Il ritaglio di un antenato come rettangolo dello schermo: `overflow` diverso da `visible` (per asse) e `clip-path: inset()` in px (1-4 valori, `round` ignorato). Un antenato senza misura (0×0: jsdom, un nodo non
 * ancora disposto) non ritaglia. Un `clip-path` che non e` un `inset()` non si sa leggere: non ritaglia (il banco del gioco lo segnala).
 */
function ritaglioDi(a: Element): { x0: number; y0: number; x1: number; y1: number } | null {
  const b = a.getBoundingClientRect();
  if (b.width === 0 && b.height === 0) return null;
  const cs = getComputedStyle(a);
  let r = { x0: -Infinity, y0: -Infinity, x1: Infinity, y1: Infinity };
  if (cs.overflowX !== 'visible') r = { ...r, x0: b.left, x1: b.right };
  if (cs.overflowY !== 'visible') r = { ...r, y0: b.top, y1: b.bottom };
  const m = /^inset\(([^)]*)\)$/.exec(cs.clipPath);
  if (m !== null) {
    const v = (m[1] ?? '').split(/\sround\s/)[0]!.trim().split(/\s+/).map((t) => (t.endsWith('px') ? parseFloat(t) : t === '0' ? 0 : NaN));
    if (v.length >= 1 && v.length <= 4 && v.every(Number.isFinite)) {
      const [alto = 0, destra = alto, basso = alto, sinistra = destra] = v;
      r = { x0: Math.max(r.x0, b.left + sinistra), y0: Math.max(r.y0, b.top + alto), x1: Math.min(r.x1, b.right - destra), y1: Math.min(r.y1, b.bottom - basso) };
    }
  }
  return r;
}

/**
 * La parte LINEARE (2×2: rotazione, scala, inclinazione) di un `transform` CALCOLATO, che il browser scrive sempre come `matrix(a, b, c, d, e, f)` o `matrix3d(…16…)` (del 3D si tiene la proiezione sul piano: m11, m12,
 * m21, m22). `none` e` l'identita`; una forma che non si sa leggere vale `null` (e il bersaglio resta assiale, come prima).
 */
function lineareDi(t: string): [number, number, number, number] | null {
  if (t === '' || t === 'none') return [1, 0, 0, 1];
  const m = /^matrix(3d)?\(([^)]*)\)$/.exec(t.trim());
  if (m === null) return null;
  const v = (m[2] ?? '').split(',').map(Number);
  if (!v.every(Number.isFinite)) return null;
  if (m[1] === '3d') return v.length === 16 ? [v[0]!, v[1]!, v[4]!, v[5]!] : null;
  return v.length === 6 ? [v[0]!, v[1]!, v[2]!, v[3]!] : null;
}

/**
 * La SAGOMA VERA di un bersaglio trasformato (kit-giochi-orcb-32-selezione, TMA-CARTE-SELEZIONE di Luca): la carta del ventaglio e` ruotata dal suo contenitore (`ManoVentaglio`, il ventaglio del Burraco), e la sua
 * scatola assiale (`getBoundingClientRect`) e` piu grande e diritta, quindi l'anello non seguiva la carta (misura del banco `carte-selezione-ruotata-banco.mjs`: spigoli fuori fino a 20 px, area fino a ×1,9).
 * Si legge dall'elemento acceso, senza niente dal gioco: la parte lineare di ogni `transform` calcolato, dal bersaglio fino al primo antenato che contiene la radice (quelli sopra muovono anche il livello delle
 * accensioni), composta; il CENTRO e` quello della scatola assiale (una trasformazione affine porta il centro nel centro); il LATO e` la misura di layout (`offsetWidth`, `offsetHeight`) per la scala.
 * Vale `null`, e il bersaglio resta ASSIALE com'era, se: la parte lineare non ruota e non inclina (anche scalata: la scatola assiale e` gia` la sagoma), il bersaglio non e` un `HTMLElement` (un nodo SVG) o non ha misura,
 * o un `transform` non si sa leggere. ⚠️ Una prospettiva dell'antenato (il Burraco: `perspective` su `.bur-mano-3d` e `rotateX(9deg)` sulla carta) non e` nel `transform` dell'elemento: si approssima affine.
 */
function sagomaRuotata(radice: HTMLElement, el: Element, r: DOMRect): { cx: number; cy: number; w: number; h: number; angolo: number } | null {
  if (!(el instanceof HTMLElement) || (r.width === 0 && r.height === 0)) return null;
  let [a, b, c, d] = [1, 0, 0, 1];
  for (let n: Element | null = el; n !== null && !n.contains(radice); n = n.parentElement) {
    const l = lineareDi(getComputedStyle(n).transform);
    if (l === null) return null;
    const [na, nb, nc, nd] = l;
    [a, b, c, d] = [na * a + nc * b, nb * a + nd * b, na * c + nc * d, nb * c + nd * d];
  }
  if (Math.abs(b) < 1e-6 && Math.abs(c) < 1e-6) return null;
  const sx = Math.hypot(a, b);
  const sy = Math.abs(a * d - b * c) / sx;
  return { cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: el.offsetWidth * sx, h: el.offsetHeight * sy, angolo: (Math.atan2(b, a) * 180) / Math.PI };
}

/** Lo `z-index` calcolato come numero (`auto` = 0): l'ordine di sovrapposizione fra fratelli. */
const zDi = (el: Element): number => {
  const z = parseInt(getComputedStyle(el).zIndex, 10);
  return Number.isFinite(z) ? z : 0;
};

/** Due fratelli «dello stesso tipo» (due carte della stessa fila, due posti della stessa pila): lo stesso tag e la stessa classe, o almeno un attributo `data-*` in comune. */
function stessoTipo(a: Element, b: Element): boolean {
  if (a.tagName !== b.tagName) return false;
  if (a.className === b.className) return true;
  for (const n of a.getAttributeNames()) if (n.startsWith('data-') && b.hasAttribute(n)) return true;
  return false;
}

/** Un elemento che si VEDE: ha un fondo (colore non trasparente o un'immagine, anche un gradiente). Un contenitore trasparente (la striscia di tocco, il posto della carta) non copre niente. */
function haFondo(el: HTMLElement): boolean {
  const cs = getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.display === 'none') return false;
  if (cs.backgroundImage !== '' && cs.backgroundImage !== 'none') return true;
  const c = cs.backgroundColor;
  return c !== '' && c !== 'transparent' && !/^rgba\([^)]*,\s*0\)$/.test(c);
}

/** La sagoma di un elemento in coordinate della radice: ruotata se un `transform` lo ruota (v. `sagomaRuotata`), se no la sua scatola. */
function sagomaDi(radice: HTMLElement, base: DOMRect, el: HTMLElement): Sagoma {
  const r = el.getBoundingClientRect();
  const raggio = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
  const s = sagomaRuotata(radice, el, r);
  if (s === null) return { cx: r.left + r.width / 2 - base.left, cy: r.top + r.height / 2 - base.top, w: r.width, h: r.height, angolo: 0, raggio };
  return { cx: s.cx - base.left, cy: s.cy - base.top, w: s.w, h: s.h, angolo: s.angolo, raggio };
}

/**
 * Le carte DAVANTI al bersaglio (TMA-CARTE-SELEZIONE rev.2 di Luca, kit-giochi-orcb-37-cornice: «se un'altra carta le si sovrappone, quella carta deve coprire anche cornice e alone, secondo lo stesso ordine di
 * sovrapposizione delle carte»). Il livello delle accensioni sta SOPRA il tavolo: senza altro l'evidenza si disegnava anche sulle carte davanti (banco `cornice-sotto-banco.mjs`). Si leggono dal DOM del gioco, senza
 * niente dal gioco: dal bersaglio fino al tavolo, a ogni antenato, i FRATELLI dello stesso tipo (`stessoTipo`) che gli stanno sopra (`z-index` maggiore, o uguale e dopo nel DOM) e ne toccano la scatola; di ognuno, le
 * sagome degli elementi che si vedono (`haFondo`: la faccia della carta, il dorso), lui compreso. Solo HTML (un bersaglio SVG non ha carte davanti). Senza misura (jsdom) non ce ne sono.
 */
function copertureDi(radice: HTMLElement, tavolo: HTMLElement | null, el: Element, r: DOMRect): Sagoma[] {
  if (tavolo === null || !(el instanceof HTMLElement) || (r.width === 0 && r.height === 0)) return [];
  const base = radice.getBoundingClientRect();
  const x0 = r.left - MARGINE_ANELLO;
  const y0 = r.top - MARGINE_ANELLO;
  const x1 = r.right + MARGINE_ANELLO;
  const y1 = r.bottom + MARGINE_ANELLO;
  const out: Sagoma[] = [];
  for (let a: HTMLElement | null = el; a !== null && a !== tavolo && a.parentElement !== null && tavolo.contains(a); a = a.parentElement) {
    const fratelli = [...a.parentElement.children];
    const ia = fratelli.indexOf(a);
    const za = zDi(a);
    fratelli.forEach((b, k) => {
      if (b === a || !(b instanceof HTMLElement) || !stessoTipo(a, b)) return;
      const zb = zDi(b);
      if (zb < za || (zb === za && k < ia)) return;
      // la scatola del fratello lontana dal bersaglio: non lo copre (i suoi figli escono di poco, l'alzata: MARGINE_ANELLO li comprende)
      const sb = b.getBoundingClientRect();
      if (sb.right <= x0 - MARGINE_ANELLO || sb.left >= x1 + MARGINE_ANELLO || sb.bottom <= y0 - MARGINE_ANELLO || sb.top >= y1 + MARGINE_ANELLO) return;
      for (const v of [b, ...b.querySelectorAll<HTMLElement>('*')]) {
        if (!(v instanceof HTMLElement) || !haFondo(v)) continue;
        const q = v.getBoundingClientRect();
        if (q.width === 0 || q.height === 0 || q.right <= x0 || q.left >= x1 || q.bottom <= y0 || q.top >= y1) continue;
        out.push(sagomaDi(radice, base, v));
      }
    });
  }
  return out;
}

/**
 * La MASCHERA di un'evidenza con carte davanti: un'immagine SVG grande quanto il suo riquadro `q`, opaca dappertutto meno che sulle sagome delle carte davanti (arrotondate e ruotate come loro). Un `<mask>` interno, non
 * un tracciato `evenodd`: due carte davanti che si sovrappongono si sommano, non si annullano.
 */
function maschera(q: Rettangolo, coperture: Sagoma[]): string {
  const n = (x: number) => Math.round(x * 100) / 100;
  const buchi = coperture
    .map((c) => {
      const x = c.cx - q.x;
      const y = c.cy - q.y;
      const giro = c.angolo === 0 ? '' : ` transform='rotate(${n(c.angolo)} ${n(x)} ${n(y)})'`;
      return `<rect x='${n(x - c.w / 2)}' y='${n(y - c.h / 2)}' width='${n(c.w)}' height='${n(c.h)}' rx='${n(c.raggio)}' fill='black'${giro}/>`;
    })
    .join('');
  const w = n(q.w);
  const h = n(q.h);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'><mask id='m' maskUnits='userSpaceOnUse' x='0' y='0' width='${w}' height='${h}'><rect width='${w}' height='${h}' fill='white'/>${buchi}</mask><rect width='${w}' height='${h}' mask='url(#m)'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Il rettangolo di un elemento in coordinate della radice. `getBoundingClientRect` c'e` anche sugli elementi SVG. Con `ritaglia` e` la parte che si VEDE: il rettangolo del bersaglio intersecato col ritaglio di ogni suo
 * antenato, fino alla radice (una mano tagliata dal bordo del tavolo ha l'anello tagliato dov'e` tagliata la carta); se non resta niente vale `null`: l'anello non si disegna. Un bersaglio senza misura (0×0: jsdom, un nodo non ancora disposto) non si ritaglia: vale il suo rettangolo 0×0, come prima del ritaglio (N2 di flotta su TMA#896).
 */
function rettangoloDi(radice: HTMLElement, el: Element | null, ritaglia = false, tavolo: HTMLElement | null = null): Rettangolo | null {
  if (el === null) return null;
  const base = radice.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  let { left: x0, top: y0, right: x1, bottom: y1 } = r;
  // il ritaglio degli antenati, da solo (senza il bersaglio): serve all'anello ruotato, che si taglia li e non sulla scatola assiale
  let taglio = { x0: -Infinity, y0: -Infinity, x1: Infinity, y1: Infinity };
  if (ritaglia && (r.width > 0 || r.height > 0)) {
    for (let a = el.parentElement; a !== null && a !== document.documentElement; a = a.parentElement) {
      const c = ritaglioDi(a);
      if (c !== null) {
        x0 = Math.max(x0, c.x0);
        y0 = Math.max(y0, c.y0);
        x1 = Math.min(x1, c.x1);
        y1 = Math.min(y1, c.y1);
        taglio = { x0: Math.max(taglio.x0, c.x0), y0: Math.max(taglio.y0, c.y0), x1: Math.min(taglio.x1, c.x1), y1: Math.min(taglio.y1, c.y1) };
      }
      if (a === radice) break;
    }
    if (x1 <= x0 || y1 <= y0) return null;
  }
  // le carte davanti (solo per le evidenze, che chiedono il ritaglio): nessuna, e il rettangolo e` quello di sempre
  const cop = ritaglia ? copertureDi(radice, tavolo, el, r) : [];
  const coperte = <T extends Rettangolo>(x: T): T => (cop.length === 0 ? x : { ...x, coperture: cop });
  const assiale = { x: x0 - base.left, y: y0 - base.top, w: x1 - x0, h: y1 - y0 };
  const s = sagomaRuotata(radice, el, r);
  if (s === null) return coperte(assiale);
  const sagoma: Rettangolo = { x: s.cx - s.w / 2 - base.left, y: s.cy - s.h / 2 - base.top, w: s.w, h: s.h, angolo: s.angolo };
  const tagliata = taglio.x0 > r.left + 0.5 || taglio.y0 > r.top + 0.5 || taglio.x1 < r.right - 0.5 || taglio.y1 < r.bottom - 0.5;
  if (!tagliata) return coperte(sagoma);
  // il lato che non taglia si apre di MARGINE_ANELLO oltre la scatola: l'anello (2 px) e l'alone (16 px) non si tagliano dove la carta non e` tagliata
  const q = { x0: Math.max(taglio.x0, r.left - MARGINE_ANELLO), y0: Math.max(taglio.y0, r.top - MARGINE_ANELLO), x1: Math.min(taglio.x1, r.right + MARGINE_ANELLO), y1: Math.min(taglio.y1, r.bottom + MARGINE_ANELLO) };
  return coperte({ ...sagoma, ritaglio: { x: q.x0 - base.left, y: q.y0 - base.top, w: q.x1 - q.x0, h: q.y1 - q.y0 } });
}

/** Quanto spazio ha l'anello ruotato oltre la scatola del bersaglio, sui lati che nessun antenato taglia: il bordo (2 px), l'alone (16 px) e il suo sfumato. */
const MARGINE_ANELLO = 32;

/** La scatola assiale di un rettangolo (anche ruotato attorno al suo centro), allargata di `MARGINE_ANELLO` per lato: il riquadro della maschera, dove stanno l'anello e l'alone. */
function scatolaAllargata(r: Rettangolo): Rettangolo {
  const a = ((r.angolo ?? 0) * Math.PI) / 180;
  const bw = Math.abs(r.w * Math.cos(a)) + Math.abs(r.h * Math.sin(a));
  const bh = Math.abs(r.w * Math.sin(a)) + Math.abs(r.h * Math.cos(a));
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  return { x: cx - bw / 2 - MARGINE_ANELLO, y: cy - bh / 2 - MARGINE_ANELLO, w: bw + 2 * MARGINE_ANELLO, h: bh + 2 * MARGINE_ANELLO };
}

/** Il ruotato «da guardare»: allargato di `ESPANSIONE_GUARDA` per lato nel SUO riferimento (lo stringe il livello, che ha `overflow-hidden`). */
const guardaRuotato = (r: Rettangolo): Rettangolo => ({ ...r, x: r.x - ESPANSIONE_GUARDA, y: r.y - ESPANSIONE_GUARDA, w: r.w + 2 * ESPANSIONE_GUARDA, h: r.h + 2 * ESPANSIONE_GUARDA });

/* eslint-disable react-refresh/only-export-components -- intentional: la geometria dell'anello e` una funzione pura esportata per il test, accanto al componente che la usa (la zona e` un elenco chiuso di file, verify-tutorial) */
/** Quanto l'anello «da guardare» sta largo oltre il bersaglio, per lato: il bordo (2) e due di aria. «tocca» ha gia` il suo `ring-2` fuori dal rettangolo. */
export const ESPANSIONE_GUARDA = 4;

/**
 * Il rettangolo dell'anello «da guardare»: il bersaglio allargato di `ESPANSIONE_GUARDA` per lato, cosi` il tratteggio circonda il bersaglio senza toccarlo (un bersaglio di solo testo non ha padding).
 * Il livello delle accensioni ha `overflow-hidden`: il lato che uscirebbe si stringe fino al bordo del livello, MA MAI DENTRO il bersaglio (al piu` coincide col suo bordo). `limiti` e` la misura del
 * livello (con origine 0,0); senza misura (jsdom) l'anello non si stringe.
 * ⛔ Caso limite dichiarato: se il bersaglio e` a meno di 4 px dal bordo del livello, il rettangolo arriva al bordo e il suo tratteggio da 2 px puo` toccare il bersaglio da quel lato; se il bersaglio
 * esce dal livello, quel lato coincide col bordo del bersaglio.
 */
export function rettangoloGuarda(r: Rettangolo, limiti: Limiti | null): Rettangolo {
  const sinistra = Math.min(r.x, Math.max(r.x - ESPANSIONE_GUARDA, limiti === null ? -Infinity : 0));
  const alto = Math.min(r.y, Math.max(r.y - ESPANSIONE_GUARDA, limiti === null ? -Infinity : 0));
  const destra = Math.max(r.x + r.w, Math.min(r.x + r.w + ESPANSIONE_GUARDA, limiti === null ? Infinity : limiti.w));
  const basso = Math.max(r.y + r.h, Math.min(r.y + r.h + ESPANSIONE_GUARDA, limiti === null ? Infinity : limiti.h));
  return { x: sinistra, y: alto, w: destra - sinistra, h: basso - alto };
}
/* eslint-enable react-refresh/only-export-components */

const uguali = (a: Rettangolo | null, b: Rettangolo | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    Math.abs(a.x - b.x) < 0.5 &&
    Math.abs(a.y - b.y) < 0.5 &&
    Math.abs(a.w - b.w) < 0.5 &&
    Math.abs(a.h - b.h) < 0.5 &&
    a.angolo === undefined === (b.angolo === undefined) &&
    Math.abs((a.angolo ?? 0) - (b.angolo ?? 0)) < 0.05 &&
    uguali(a.ritaglio ?? null, b.ritaglio ?? null) &&
    stesseCoperture(a.coperture, b.coperture));

const stesseCoperture = (a: Sagoma[] | undefined, b: Sagoma[] | undefined): boolean =>
  a === b ||
  (a !== undefined &&
    b !== undefined &&
    a.length === b.length &&
    a.every((x, i) => {
      const y = b[i]!;
      return Math.abs(x.cx - y.cx) < 0.5 && Math.abs(x.cy - y.cy) < 0.5 && Math.abs(x.w - y.w) < 0.5 && Math.abs(x.h - y.h) < 0.5 && Math.abs(x.angolo - y.angolo) < 0.05 && Math.abs(x.raggio - y.raggio) < 0.5;
    }));

/** La rotazione di un rettangolo nel `transform` del fantasma: niente per un bersaglio assiale (il `transform` di sempre); `rotate(0deg)` se ruota solo l'altro capo, perche` il movimento giri. */
const rotazione = (r: Rettangolo, altro: Rettangolo | null): string => (r.angolo === undefined && altro?.angolo === undefined ? '' : ` rotate(${r.angolo ?? 0}deg)`);

/** La misura del livello delle accensioni (la radice, origine 0,0). */
export interface Limiti {
  w: number;
  h: number;
}
interface Misurati {
  evidenze: (Rettangolo | null)[];
  da: Rettangolo | null;
  a: Rettangolo | null;
  limiti: Limiti | null;
}
const NESSUNO: Misurati = { evidenze: [], da: null, a: null, limiti: null };
const stessiLimiti = (a: Limiti | null, b: Limiti | null): boolean => a === b || (a !== null && b !== null && Math.abs(a.w - b.w) < 0.5 && Math.abs(a.h - b.h) < 0.5);
const stessiMisurati = (x: Misurati, y: Misurati): boolean =>
  x.evidenze.length === y.evidenze.length && x.evidenze.every((r, i) => uguali(r, y.evidenze[i] ?? null)) && uguali(x.da, y.da) && uguali(x.a, y.a) && stessiLimiti(x.limiti, y.limiti);

/** Gli eventi con cui un bersaglio che si sposta da solo (transizione o animazione CSS) dice che ha finito: salgono fino al tavolo. */
const FINE_MOVIMENTO = ['transitionend', 'transitioncancel', 'animationend', 'animationcancel'] as const;

export interface PropsAccensioni<B> {
  /** Il contenitore posizionato dentro cui si disegna (le coordinate sono le sue). */
  radice: RefObject<HTMLElement | null>;
  /** Il contenitore del tavolo vero, dove il gioco cerca i suoi bersagli. */
  tavolo: RefObject<HTMLElement | null>;
  bersaglio: (b: B, tavolo: HTMLElement) => Element | null;
  evidenze: readonly B[];
  /** Le accensioni ci sono solo mentre si aspetta la mossa, o in un passo di sola lettura. */
  accese: boolean;
  /** Passo di sola lettura: le evidenze sono «da guardare», non da toccare (bordo tratteggiato neutro, senza bagliore: come la riga muta, dice «qui non si tocca»). */
  daGuardare?: boolean;
  mostra: { da: B; a: B } | undefined;
  /** «Mostramelo» acceso: il fantasma scorre da `da` ad `a`, in ciclo; col movimento ridotto resta una freccia ferma. */
  fantasma: boolean;
  rendiFantasma?: (b: B) => ReactNode;
}

/** Le accensioni sui bersagli del passo e, a richiesta, il fantasma che mostra la mossa. Sopra il tavolo, senza ricevere tocchi: il tavolo non si tocca e non si rimonta. */
export function Accensioni<B>({ radice, tavolo, bersaglio, evidenze, accese, daGuardare = false, mostra, fantasma, rendiFantasma }: PropsAccensioni<B>) {
  const [misurati, setMisurati] = useState<Misurati>(NESSUNO);
  const movimentoRidotto = useMovimentoRidotto();
  const fantasmaRef = useRef<HTMLDivElement | null>(null);

  const misura = useCallback(() => {
    const r = radice.current;
    const t = tavolo.current;
    if (r === null || t === null || !accese) {
      setMisurati((prima) => (stessiMisurati(prima, NESSUNO) ? prima : NESSUNO));
      return;
    }
    const m: Misurati = {
      evidenze: evidenze.map((b) => rettangoloDi(r, bersaglio(b, t), true, t)),
      da: mostra === undefined ? null : rettangoloDi(r, bersaglio(mostra.da, t)),
      a: mostra === undefined ? null : rettangoloDi(r, bersaglio(mostra.a, t)),
      limiti: r.clientWidth > 0 && r.clientHeight > 0 ? { w: r.clientWidth, h: r.clientHeight } : null,
    };
    setMisurati((prima) => (stessiMisurati(prima, m) ? prima : m));
  }, [radice, tavolo, bersaglio, evidenze, mostra, accese]);

  // dopo ogni render e ogni volta che il tavolo cambia sotto di noi (la sua misura, lo scorrimento, la finestra, il DOM del gioco). Un bersaglio che `bersaglio()` non trova (null) non si
  // illumina e non rompe niente: si riprova a ogni render del tavolo e a ogni cambio del suo DOM (il MutationObserver sta SOLO sul contenitore del tavolo, mai su document; niente polling).
  // Un bersaglio che si SPOSTA da solo (la carta scelta che si alza con una transizione CSS) cambia posto SENZA nessun cambio di DOM dopo il primo: si rimisura anche alla FINE della sua transizione
  // o animazione (gli eventi salgono fino al tavolo; `transitioncancel`/`animationcancel` se si interrompe). Nessuna animazione nuova: col movimento ridotto non c'e` transizione e il posto nuovo e`
  // gia` quello giusto al cambio di DOM.
  useLayoutEffect(() => {
    misura();
  });
  useEffect(() => {
    const r = radice.current;
    const t = tavolo.current;
    if (r === null || t === null) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(misura) : null;
    ro?.observe(r);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(misura) : null;
    mo?.observe(t, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'transform'] });
    r.addEventListener('scroll', misura, true);
    window.addEventListener('resize', misura);
    for (const evento of FINE_MOVIMENTO) t.addEventListener(evento, misura);
    return () => {
      ro?.disconnect();
      mo?.disconnect();
      r.removeEventListener('scroll', misura, true);
      window.removeEventListener('resize', misura);
      for (const evento of FINE_MOVIMENTO) t.removeEventListener(evento, misura);
    };
  }, [radice, tavolo, misura]);

  const { da, a } = misurati;
  const animato = fantasma && !movimentoRidotto;
  useEffect(() => {
    const el = fantasmaRef.current;
    if (!animato || el === null || da === null || a === null || typeof el.animate !== 'function') return;
    const verso = (r: Rettangolo, altro: Rettangolo) => `translate(${Math.round(r.x)}px, ${Math.round(r.y)}px)${rotazione(r, altro)}`;
    const animazione = el.animate([{ transform: verso(da, a) }, { transform: verso(a, da) }], { duration: 1100, iterations: Infinity, easing: 'ease-in-out' });
    return () => animazione.cancel();
  }, [animato, da, a]);

  if (!accese) return null;
  const gradi = da !== null && a !== null ? (Math.atan2(a.y - da.y, a.x - da.x) * 180) / Math.PI : 0;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {misurati.evidenze.map((bersaglioMisurato, i) => {
        if (bersaglioMisurato === null) return null;
        const r0 = !daGuardare ? bersaglioMisurato : bersaglioMisurato.angolo === undefined ? rettangoloGuarda(bersaglioMisurato, misurati.limiti) : guardaRuotato(bersaglioMisurato);
        const r: Rettangolo = { ...r0, ritaglio: bersaglioMisurato.ritaglio, coperture: bersaglioMisurato.coperture };
        const classe = daGuardare
          ? 'absolute rounded-md border-2 border-dashed border-fg/70 bg-fg/5'
          : 'absolute rounded-md bg-brand/10 shadow-[0_0_16px_1px_color-mix(in_srgb,var(--color-brand)_35%,transparent)] ring-2 ring-brand';
        // un bersaglio assiale: lo span di sempre, senza `transform`. Uno ruotato: lo span gira con la carta, attorno al suo centro (l'origine di sempre, 50% 50%)
        const ruota = r.angolo === undefined ? undefined : `rotate(${r.angolo}deg)`;
        const cop = r.coperture;
        if (cop !== undefined && cop.length > 0) {
          // con carte DAVANTI: l'anello dentro un INVOLUCRO (il ritaglio, o la scatola allargata di MARGINE_ANELLO) mascherato sulle loro sagome, che lo coprono come coprono la carta. L'evidenza
          // (`data-evidenza-tutorial`) resta l'ANELLO stesso, con la scatola di sempre: chi la misura (i banchi dei giochi) legge lo stesso rettangolo; l'involucro porta solo la maschera
          const qm = r.ritaglio ?? scatolaAllargata(r);
          const maschera_ = maschera(qm, cop);
          // RITAGLIATA E COPERTA (TMA#944, kit-giochi-orcb-38-cornice): con `r.ritaglio` l'evidenza resta sull'INVOLUCRO, con la scatola del ritaglio, come senza carte davanti (il ramo `r.ritaglio` qui sotto, e
          // main 927bf1cb): i banchi che misurano `[data-evidenza-tutorial]` (Poker Cinese, la plancia mini che ritaglia) leggono lo stesso rettangolo di prima. Senza ritaglio l'evidenza e` l'anello: e` la
          // scatola di sempre (il banco della Briscola misura 0 px fra l'evidenza e la faccia), e l'involucro allargato di MARGINE_ANELLO non e` una scatola che qualcuno abbia mai misurato
          const evidenza = { 'data-evidenza-tutorial': i, 'data-aspetto': daGuardare ? 'guarda' : 'tocca', 'data-coperta': cop.length };
          const sulRitaglio = r.ritaglio !== undefined;
          return (
            <span
              key={i}
              data-evidenza-maschera={cop.length}
              {...(sulRitaglio ? evidenza : {})}
              className={r.ritaglio === undefined ? 'absolute' : 'absolute overflow-hidden'}
              style={{ left: qm.x, top: qm.y, width: qm.w, height: qm.h, maskImage: maschera_, WebkitMaskImage: maschera_, maskSize: '100% 100%', WebkitMaskSize: '100% 100%', maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat' }}
            >
              <span
                {...(sulRitaglio ? {} : evidenza)}
                data-anello
                className={classe}
                style={{ left: r.x - qm.x, top: r.y - qm.y, width: r.w, height: r.h, transform: ruota }}
              />
            </span>
          );
        }
        const q = r.ritaglio;
        if (q === undefined) {
          return <span key={i} data-evidenza-tutorial={i} data-aspetto={daGuardare ? 'guarda' : 'tocca'} className={classe} style={{ left: r.x, top: r.y, width: r.w, height: r.h, transform: ruota }} />;
        }
        // ruotato e tagliato da un antenato: l'anello ruotato dentro un riquadro che taglia dove taglia l'antenato
        return (
          <span key={i} data-evidenza-tutorial={i} data-aspetto={daGuardare ? 'guarda' : 'tocca'} className="absolute overflow-hidden" style={{ left: q.x, top: q.y, width: q.w, height: q.h }}>
            <span data-anello className={classe} style={{ left: r.x - q.x, top: r.y - q.y, width: r.w, height: r.h, transform: ruota }} />
          </span>
        );
      })}
      {fantasma && da !== null && a !== null && mostra !== undefined && (
        movimentoRidotto ? (
          <span
            data-fantasma-tutorial="freccia"
            className="absolute flex size-8 items-center justify-center rounded-full bg-brand text-brand-fg"
            style={{ left: (da.x + da.w / 2 + a.x + a.w / 2) / 2 - 16, top: (da.y + da.h / 2 + a.y + a.h / 2) / 2 - 16, transform: `rotate(${gradi}deg)` }}
          >
            <ArrowRight size={18} />
          </span>
        ) : (
          <div
            ref={fantasmaRef}
            data-fantasma-tutorial="carta"
            className="absolute left-0 top-0 overflow-hidden rounded-md border-2 border-brand bg-brand/25 opacity-80"
            style={{ width: da.w, height: da.h, transform: `translate(${Math.round(da.x)}px, ${Math.round(da.y)}px)${rotazione(da, a)}` }}
          >
            {rendiFantasma?.(mostra.da)}
          </div>
        )
      )}
    </div>
  );
}
